/* ONE CELL AT A TIME, WITH A SEARCH — Max, 2026-10-12, on the Daily Output grid: "tôi muốn chọn từng ô
   1 chứ không phải chọn 1 cái liên tục á. Vì đôi khi chọn tôi cũng có thể search nè chứ hiện tại phải
   ra theo danh sách dropdown thì nó lâu".

   The row used to be chosen through one popup that walked Month > PO# > Description > Colour >
   Skin/Case in that order, a list at each step. Now each of the five cells is its own search box with
   its own list, in ANY order: type "sunset" into Description first and Month, PO# and Colour narrow to
   what that item is on. The lists come from the same /options endpoint the popup used — it already
   takes any of the other four as a filter — so the server decides what goes together, exactly as
   before. A cell whose list has one answer fills itself (`settlePicks`); Skin/Case opens only once the
   other four are set, because it is the choice that names the PO line, and picking it finishes the
   row: a blank row is added, an unmatched row is attached.

   THE LIST IS A PORTAL, fixed to the input's own rectangle — every cell on this table clips at 34px
   and the frozen ones sit in their own stacking contexts, which is how the old popup came to paint
   nothing (see Cascade). Above the sidebar (950) and the header (1000).

   A CELL CAN ARRIVE FILLED WITH A GUESS (Max, same day: "nếu thể hiện như vầy là tôi phải gõ lại từng
   cái á huhu"): an unmatched row opens with what the vendor's words point at, from
   /unmatched/suggest. `guess` tints it — blue for a name that matched, amber for one found only by a
   similar name — until the person picks that cell themselves; `prefer` lights the option the vendor's
   file named (its Skin/Case), so Enter takes it. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export const PICK_FIELDS = ['month', 'po', 'description', 'colour'];

/* A WRONG CELL CAN BE CHANGED WITHOUT UNPICKING THE ROW — Max, 2026-10-13, on a row filed under
   2025-01 · S25 BUY#2 SLT that belonged to S26: "dòng này tôi biết nó sai nhưng tôi lại ko thể xóa để
   chỉnh lại á". Each list is narrowed by the other three cells, so on a filled row Month offered only
   its own month (the PO allows no other) and PO# only its own PO (the month allows no other) — two cells
   holding each other in place. Under its own list each cell now also offers the item's OTHER choices,
   narrowed only by what says which item it is (RELAX); choosing one of those clears the cells it no
   longer fits (the rest of PICK_FIELDS), and the one-answer settle fills them back where it can. */
export const RELAX = { month: ['description', 'colour'], po: ['description', 'colour'], colour: ['description'], description: [] };
// Dictionary keys (src/locales/xOutput.js); read through t() where they are drawn.
const FIELD_LABEL = { month: 'xOutput.colMonth', po: 'xOutput.colPo', description: 'xOutput.colDescription', colour: 'xOutput.colColour' };
const n0 = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString('en-GB') : '');

/* The four that identify an item, minus the one being asked about. `undefined` is "not chosen";
   an empty colour is a choice (some lines carry none). */
export const filtersFor = (picks, field) => Object.fromEntries(PICK_FIELDS
  .filter((f) => f !== field && picks[f] !== undefined && picks[f] !== '')
  .map((f) => [f, picks[f]]));

/* After a pick: drop any other choice the new one has ruled out, and fill any cell left with a single
   answer. Two passes, because filling one can leave another with one. */
export async function settlePicks(picks, fetchOptions, hold = []) {
  const pass = async (from) => {
    const lists = await Promise.all(PICK_FIELDS.map((f) => fetchOptions(f, filtersFor(from, f)).catch(() => null)));
    const next = { ...from };
    PICK_FIELDS.forEach((f, i) => {
      if (!lists[i]) return;
      const values = lists[i].map((o) => o.value);
      if (next[f] !== undefined && !values.includes(next[f])) next[f] = undefined;
      // A cell the person has just emptied is not filled straight back (`hold`).
      else if (next[f] === undefined && values.length === 1 && !hold.includes(f)) [next[f]] = values;
    });
    return next;
  };
  const first = await pass(picks);
  const same = (a, b) => PICK_FIELDS.every((f) => a[f] === b[f]);
  return same(first, picks) ? first : pass(first);
}

/* THE TEXT IN A CELL CAN BE READ WHOLE AND COPIED — Max, 2026-10-13: "ko thể click vào ô để xem text đầy
   đủ hay copy á". Opening a cell used to empty it — what it held went to the placeholder, greyed and cut
   at the cell's edge, and a placeholder cannot be selected. Now the box opens HOLDING its value, selected,
   so Ctrl+C copies it and typing replaces it; the list is not narrowed by that value until somebody types
   (`typed`), or opening a filled cell would show one option. Above the list, the value in full and — on
   a not-matched row — the vendor's own wording and the reason (`detail`), as selectable text with a Copy
   button: the hints under the cells are cut to one line, and a hover cannot be copied. */
async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* not a secure context */ }
  const ta = document.createElement('textarea');
  ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
  document.body.removeChild(ta);
  return ok;
}

export function CellCombo({ field, value, filters, fetchOptions, onPick, placeholder, disabled, title, wide, guess, prefer, detail, widerFilters }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState(false);
  const [copied, setCopied] = useState('');
  const [opts, setOpts] = useState(null);
  const [wider, setWider] = useState([]);
  const [err, setErr] = useState('');
  const [hi, setHi] = useState(0);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const popRef = useRef(null);
  // The click-away listener is bound once per opening; these let it see the box as it is now.
  const emptiedRef = useRef(false);
  const clearRef = useRef(() => {});
  const key = JSON.stringify(filters || {});
  // Only worth asking when it drops a filter the cell's own list has.
  const widerKey = widerFilters && JSON.stringify(widerFilters) !== key ? JSON.stringify(widerFilters) : '';

  useEffect(() => {
    if (!open) return undefined;
    let dead = false;
    setOpts(null); setErr('');
    fetchOptions(field, filters || {})
      .then((o) => {
        if (dead) return;
        setOpts(o);
        if (prefer) {
          const at = o.findIndex((x) => String(field === 'part' ? x.part : x.label).toLowerCase() === String(prefer).toLowerCase());
          if (at >= 0) setHi(at);
        } else if (value !== undefined && field !== 'part') {
          // Opened on a filled cell, the list lights what it holds, so Enter keeps it.
          const at = o.findIndex((x) => x.value === value);
          if (at >= 0) setHi(at);
        }
      })
      .catch((e) => { if (!dead) { setOpts([]); setErr(e.message); } });
    setWider([]);
    if (widerKey) {
      fetchOptions(field, widerFilters).then((o) => { if (!dead) setWider(o); }).catch(() => {});
    }
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, field, key, widerKey]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => setRect(inputRef.current ? inputRef.current.getBoundingClientRect() : null);
    place();
    const away = (e) => {
      if (inputRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      if (emptiedRef.current) { clearRef.current(); return; }
      setOpen(false); setQ('');
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', away);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', away);
    };
  }, [open]);

  const labelOf = (o) => (field === 'part' ? o.part : (o.label || t('xOutput.blank')));
  const words = typed ? q.toLowerCase().split(/\s+/).filter(Boolean) : [];
  const matches = (o) => words.every((w) => labelOf(o).toLowerCase().includes(w));
  const shown = (opts || []).filter(matches);
  const own = new Set((opts || []).map((o) => o.value));
  const shownWider = (opts ? wider : []).filter((o) => !own.has(o.value) && matches(o));
  // One list for the keys: the cell's own choices, then the item's other ones.
  const all = [...shown.map((o) => ({ o, w: false })), ...shownWider.map((o) => ({ o, w: true }))];
  const take = (o, w) => {
    setOpen(false); setQ(''); setTyped(false);
    onPick(field === 'part' ? o : o.value, w ? { wider: true } : undefined);
  };
  const clears = PICK_FIELDS.filter((f) => f !== field && !(RELAX[field] || []).includes(f)).map((f) => t(FIELD_LABEL[f]));
  // Said out loud, because a wrong one of THESE is why a choice is missing (Max, 2026-10-13: a row
  // whose Colour read "Coyote Tan (S20)", S25's name, offered no 2026 month).
  const narrowedBy = Object.keys(widerFilters || {}).map((f) => `${t(FIELD_LABEL[f])} "${widerFilters[f]}"`);
  /* EMPTYING A CELL EMPTIES IT — Max, 2026-10-13: "delete thì nó cũng ko mất". Backspace to nothing and
     Enter (or leave the cell, or press Clear above the list) and the cell is cleared; Escape still puts
     the value back. The settle right after does not fill it straight back. */
  const clearCell = () => {
    setOpen(false); setQ(''); setTyped(false);
    if (value !== undefined && field !== 'part') onPick(undefined, { clear: true });
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, Math.max(all.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (typed && !q.trim()) clearCell();
      else if (open && all[hi]) take(all[hi].o, all[hi].w);
    }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); setTyped(false); }
    else if (e.key === 'Tab') { if (typed && !q.trim()) clearCell(); else { setOpen(false); setQ(''); setTyped(false); } }
  };

  const shownValue = value === undefined ? '' : (value === '' ? t('xOutput.blank') : value);
  // Open holding the value, selected — after the click has placed its caret, or it undoes the selection.
  const openHere = () => {
    if (open) return;
    setOpen(true); setHi(0); setTyped(false); setQ(value === undefined || value === '' ? '' : String(value));
    setTimeout(() => { try { inputRef.current?.select(); } catch (e) { /* gone */ } }, 0);
  };
  /* [id, label, text]: the id is what the code keys on (the Copy flash, the Clear button), the label is
     what the reader sees — so a translated label cannot change which line carries Clear. */
  const lines = [
    ...(value !== undefined && value !== '' ? [['cell', t('xOutput.thisCell'), String(value)]] : []),
    ...((detail || []).filter(([, , text]) => text)),
  ];
  emptiedRef.current = open && typed && !q.trim() && value !== undefined;
  clearRef.current = clearCell;
  const copy = async (id, text) => {
    if (await copyText(text)) { setCopied(id); setTimeout(() => setCopied(''), 1200); }
  };
  const W = Math.max(rect ? rect.width : 0, wide ? 380 : 260);
  /* NEAR THE FOOT OF THE SCREEN THE LIST OPENS UPWARD, AND ITS BOTTOM EDGE IS WHAT IS PINNED — Max,
     2026-10-12: "lâu lâu bị chạy lên trên vầy nè". It used to set `top` to the input's top minus the
     list's MAXIMUM height, 300px; a list is only as tall as its options, so Skin/Case's two rows
     floated ~230px above the cell they belong to, over rows they have nothing to do with. Anchoring
     `bottom` keeps it against the input whatever its height, and either way the height is capped at
     the room actually there, so it never runs off the screen. */
  const H = 300;
  let pos = null;
  if (rect) {
    const below = window.innerHeight - rect.bottom - 10;
    const above = rect.top - 10;
    const up = below < Math.min(H, 160) && above > below;
    pos = {
      left: Math.max(8, Math.min(rect.left, window.innerWidth - W - 8)),
      width: W,
      ...(up
        ? { bottom: window.innerHeight - rect.top + 2, maxHeight: Math.min(H, above) }
        : { top: rect.bottom + 2, maxHeight: Math.min(H, Math.max(below, 120)) }),
    };
  }

  return (
    <>
      <input
        ref={inputRef}
        className={`co-combo${value !== undefined ? ' co-combo-set' : ''}${guess ? ` co-combo-${guess}` : ''}`}
        data-field={field}
        value={open ? q : shownValue}
        placeholder={open ? (shownValue || placeholder) : placeholder}
        disabled={disabled}
        title={title || (guess === 'loose' ? t('xOutput.guessedLoose', { value: shownValue })
          : guess ? t('xOutput.guessedFile', { value: shownValue }) : '') || shownValue || placeholder || ''}
        onFocus={openHere}
        onClick={openHere}
        onChange={(e) => { setQ(e.target.value); setTyped(true); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && pos && createPortal(
        <div ref={popRef} className="co-combo-pop" style={pos} role="listbox">
          {lines.length > 0 && (
            <div className="co-combo-full">
              {lines.map(([id, label, text]) => (
                <div key={id} className="co-combo-fullrow">
                  <span className="co-combo-fulllabel">{label}</span>
                  <span className="co-combo-fulltext">{text}</span>
                  <span className="co-combo-fullbtns">
                    <button type="button" className="co-combo-copy" title={t('xOutput.copyTitle', { text })} onClick={() => copy(id, text)}>
                      {copied === id ? t('xOutput.copied') : t('xOutput.copy')}
                    </button>
                    {id === 'cell' && field !== 'part' && (
                      <button type="button" className="co-combo-copy co-combo-clear" title={t('xOutput.emptyCellTitle')} onClick={clearCell}>{t('xOutput.clear')}</button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          {!!err && <div className="co-pick-empty">{err}</div>}
          {opts === null && <div className="co-pick-empty">{t('xOutput.loading')}</div>}
          {opts !== null && !all.length && !err && (
            <div className="co-pick-empty">{opts.length ? t('xOutput.nothingMatchesShorter') : t('xOutput.nothingLeft')}</div>
          )}
          {all.map(({ o, w }, i) => (
            <React.Fragment key={`${w ? 'w' : 'o'}|${field === 'part' ? o.part : o.value}`}>
            {w && i === shown.length && (
              <div className="co-opt-wider">
                {narrowedBy.length
                  ? t('xOutput.otherChoicesWith', { what: narrowedBy.join(t('xOutput.andJoin')) })
                  : t('xOutput.otherChoices')}
                {clears.length ? ` — ${t('xOutput.pickingClears', { fields: clears.join(t('xOutput.andJoin')) })}` : ''}
              </div>
            )}
            <button
              type="button"
              role="option"
              aria-selected={i === hi}
              className={`co-opt${field === 'part' ? ' co-opt-part' : ''}${w ? ' co-opt-w' : ''}${i === hi ? ' co-opt-hi' : ''}`}
              onMouseEnter={() => setHi(i)}
              onClick={() => take(o, w)}
            >
              <span>{labelOf(o)}</span>
              <em>
                {field === 'part'
                  ? `${t('xOutput.leftOf', { left: n0(o.remainQty), order: n0(o.orderQty) })}${o.lines > 1 ? ` · ${t('xOutput.destinations', { n: o.lines })}` : ''}`
                  : o.n}
              </em>
            </button>
            </React.Fragment>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
